import React from 'react';
import { ClassicCaptionPage } from './ClassicCaptionPage';
import { CaptionPageProps } from './ClassicCaptionPage';
import { VideoCanvas, Position } from '../../DesignSystem';
import { CaptionPage } from '../CaptionGrouper';

type MaxLines = "one_word" | "one_line" | "two_lines" | "three_lines";

interface ClassicCaptionPreviewProps extends CaptionPageProps {
    maxLines?: MaxLines;
    compact?: boolean;
}

const PREVIEW_DURATION = 3000; // 3 seconds in milliseconds

export const ClassicCaptionPreview: React.FC<ClassicCaptionPreviewProps> = (props) => {
    const {
        page,
        fontFamily,
        canvas,
        primaryColor,
        currentSpokenWordColor,
        maxFontSize,
        outline,
        background,
        textAnimation,
        borderRadius,
        captionCase,
        hidePunctuation,
        position = 'bottom center',
        containerPadding = { x: 0.1, y: 0.05 },
        boxPadding = { x: 0, y: 0 },
        maxLines = "two_lines",
        compact = false
    } = props;

    // Create state for animation frame
    const [frame, setFrame] = React.useState(0);

    // Set up animation loop
    React.useEffect(() => {
        let animationFrame: number;
        const animate = () => {
            setFrame(f => (f + 1) % 150); // 150 frames = 5 seconds at 30fps
            animationFrame = requestAnimationFrame(animate);
        };
        animate();
        return () => cancelAnimationFrame(animationFrame);
    }, []);

    // Create preview canvas with better dimensions
    const previewCanvas: VideoCanvas = {
        ...canvas,
        width: compact ? 200 : Math.max(400, canvas.width),
        height: compact ? 60 : Math.max(200, canvas.height),
        aspectRatio: "16:9", // Keep 16:9 but adjust dimensions for compact mode
        safeZone: {
            top: 0.1,
            bottom: 0.1,
            left: 0.1,
            right: 0.1
        }
    };

    // Create mock video config with better timing
    const mockVideoConfig = {
        fps: 30,
        width: previewCanvas.width,
        height: previewCanvas.height,
        durationInFrames: 150,
        id: 'preview'
    };

    // Use a smaller font size for better visibility
    const adjustedMaxFontSize = Math.floor(maxFontSize * (compact ? 0.5 : 0.7));

    // Split text into words
    const words = (page.text || '').split(/\s+/).filter(word => word.length > 0);

    // Helper function to create preview pages based on maxLines
    const createPreviewPages = (words: string[], maxLines: MaxLines): CaptionPage[] => {
        if (compact) {
            // In compact mode, show "Caption preview" as two tokens for animation
            return [{
                text: "Caption preview",
                startMs: 0,
                tokens: [
                    {
                        text: "Caption",
                        fromMs: 0,
                        toMs: PREVIEW_DURATION / 2
                    },
                    {
                        text: "preview",
                        fromMs: PREVIEW_DURATION / 2,
                        toMs: PREVIEW_DURATION
                    }
                ]
            }];
        }

        if (maxLines === "one_word") {
            return [{
                text: "Caption",
                startMs: 0,
                tokens: [{
                    text: "Caption",
                    fromMs: 0,
                    toMs: 500
                }]
            }];
        }

        if (maxLines === "one_line") {
            return [{
                text: "Caption preview",
                startMs: 0,
                tokens: [{
                    text: "Caption preview",
                    fromMs: 0,
                    toMs: PREVIEW_DURATION
                }]
            }];
        }

        // For two or three lines
        return [{
            text: "Caption\npreview",
            startMs: 0,
            tokens: [
                {
                    text: "Caption",
                    fromMs: 0,
                    toMs: PREVIEW_DURATION / 2
                },
                {
                    text: "preview",
                    fromMs: PREVIEW_DURATION / 2,
                    toMs: PREVIEW_DURATION
                }
            ]
        }];
    };

    const previewPages = createPreviewPages(words, maxLines);

    // Adjust canvas height based on number of lines
    const adjustedCanvas: VideoCanvas = {
        ...previewCanvas,
        height: compact ? previewCanvas.height : Math.max(200 * previewPages.length, previewCanvas.height)
    };

    // Calculate positions for each line
    const getPosition = (index: number, total: number): Position => {
        if (compact || total === 1) return "center center";
        if (total === 2) return index === 0 ? "top center" : "bottom center";
        // For three lines
        if (index === 0) return "top center";
        if (index === 1) return "center center";
        return "bottom center";
    };

    return (
        <div style={{
            width: '100%',
            aspectRatio: compact ? '10/3' : '16/9',
            backgroundColor: 'rgba(0, 0, 0, 0.05)',
            borderRadius: '0px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
        }}>
            {previewPages.map((previewPage, index) => (
                <ClassicCaptionPage
                    key={index}
                    {...props}
                    page={previewPage}
                    canvas={adjustedCanvas}
                    maxFontSize={adjustedMaxFontSize}
                    videoConfig={mockVideoConfig}
                    currentFrame={frame}
                    position={getPosition(index, previewPages.length)}
                />
            ))}
        </div>
    );
}; 